/**
 * Scoring for sort-type rounds (Timeslip, Crowflight R1/R2). See spec §6.2.
 *
 * Green cards are those belonging to a largest group whose relative order
 * (as the player arranged them) already matches the true order — i.e. the
 * longest strictly increasing subsequence of true positions, indexed by the
 * player's placement order. Ties are broken first by how many cards in the
 * candidate group sit at their exact correct position, then by the
 * lexicographically earliest set of player-order indices.
 */
/**
 * @param truePositionsInPlayerOrder The true (1-based) position of each card,
 *   listed in the order the player placed the cards. E.g. if the player's
 *   order is [cardE, cardB, ...] and cardE truly belongs at position 5,
 *   cardB at position 2, the array starts [5, 2, ...].
 */
export function scoreSortRound(truePositionsInPlayerOrder, pointsPerItem) {
    const n = truePositionsInPlayerOrder.length;
    const greenIndices = longestOrderedGroup(truePositionsInPlayerOrder);
    const greenSet = new Set(greenIndices);
    const redIndices = [];
    for (let i = 1; i <= n; i++) {
        if (!greenSet.has(i))
            redIndices.push(i);
    }
    return {
        greenIndices,
        redIndices,
        pointsPerItem,
        totalPoints: pointsPerItem * greenIndices.length,
    };
}
/**
 * Returns the 1-based player-order indices of the winning longest increasing
 * subsequence of `values`, per the tie-break rules in spec §6.2.
 */
export function longestOrderedGroup(values) {
    const n = values.length;
    if (n === 0)
        return [];
    // L[i] = length of the longest increasing subsequence ending at index i (0-based).
    const L = new Array(n).fill(1);
    for (let i = 0; i < n; i++) {
        for (let j = 0; j < i; j++) {
            if (values[j] < values[i] && L[j] + 1 > L[i]) {
                L[i] = L[j] + 1;
            }
        }
    }
    const maxLen = Math.max(...L);
    // Enumerate all index-sets (0-based) achieving maxLen via backtracking.
    const candidates = [];
    const endIndices = L.reduce((acc, len, idx) => {
        if (len === maxLen)
            acc.push(idx);
        return acc;
    }, []);
    function backtrack(i, path) {
        const newPath = [i, ...path];
        if (L[i] === 1) {
            candidates.push(newPath);
            return;
        }
        for (let j = i - 1; j >= 0; j--) {
            if (values[j] < values[i] && L[j] === L[i] - 1) {
                backtrack(j, newPath);
            }
        }
    }
    for (const end of endIndices)
        backtrack(end, []);
    // Rank candidates: most exact-position matches first, then lexicographically
    // earliest (0-based indices, ascending), then take the winner.
    let best = candidates[0];
    let bestExact = countExactMatches(best, values);
    for (let k = 1; k < candidates.length; k++) {
        const cand = candidates[k];
        const exact = countExactMatches(cand, values);
        if (exact > bestExact || (exact === bestExact && lexCompare(cand, best) < 0)) {
            best = cand;
            bestExact = exact;
        }
    }
    return best.map((i) => i + 1);
}
function countExactMatches(indices0Based, values) {
    let count = 0;
    for (const i of indices0Based) {
        if (values[i] === i + 1)
            count++;
    }
    return count;
}
function lexCompare(a, b) {
    const len = Math.min(a.length, b.length);
    for (let i = 0; i < len; i++) {
        if (a[i] !== b[i])
            return a[i] - b[i];
    }
    return a.length - b.length;
}
//# sourceMappingURL=sortDiff.js.map